import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Flex, Input, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import {
  fetchFeeCodes, fetchFinanceCompanies, setFeeCode, setFinanceCompanyCode,
  type FeeCode, type FinanceCompany,
} from './api';

const { Text } = Typography;

interface CodeRowProps {
  name: string;
  /** The code now saved, or null when there is none. */
  current: string | null;
  missing: string | null;
  saving: boolean;
  /** The column's width: 30 for a finance company's FIN code, 60 for a fee code. */
  maxLength: number;
  /** Called once saved; the typed code stays until then, so a refusal loses nothing. */
  onSave: (itemCode: string, saved: () => void) => void;
}

/**
 * One AutoCount item code. Typing a different code shows what will change
 * before the Save button is pressed (CLAUDE.md §10).
 */
function CodeRow({ name, current, missing, saving, maxLength, onSave }: CodeRowProps): ReactNode {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const next = draft.trim();
  const changes = next !== '' && next !== (current ?? '');

  return (
    <Flex vertical gap={4}>
      <Flex gap={8} align="center" wrap>
        <Text style={{ minWidth: 140 }}>{name}</Text>
        <Input
          style={{ width: 200 }}
          aria-label={t('accounting.codeFor', { name })}
          placeholder={current ?? ''}
          maxLength={maxLength}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); }}
        />
        <Button
          type="primary"
          aria-label={t('accounting.saveCodeFor', { name })}
          disabled={!changes}
          loading={saving}
          onClick={() => { onSave(next, () => { setDraft(''); }); }}
        >
          {t('accounting.saveCode')}
        </Button>
      </Flex>
      {missing !== null && current === null && <Text type="warning">{missing}</Text>}
      {changes && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {current === null
            ? t('accounting.codeWillSet', { name, code: next })
            : t('accounting.codeWillChange', { name, from: current, to: next })}
        </Text>
      )}
    </Flex>
  );
}

function sectionError(error: unknown, forbidden: string, failed: string): ReactNode {
  const denied = error instanceof ApiError && error.status === 403;
  return <Text type={denied ? 'secondary' : 'danger'}>{denied ? forbidden : failed}</Text>;
}

/** The AutoCount item codes finance's bike invoices are keyed under. */
export function CodeSettings(): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const companies = useQuery({ queryKey: ['accounting', 'finance-codes'], queryFn: fetchFinanceCompanies });
  const fees = useQuery({ queryKey: ['accounting', 'fee-codes'], queryFn: fetchFeeCodes });

  const refresh = (): void => {
    // A new code can release bike invoices that were waiting, so the preview refreshes too.
    void queryClient.invalidateQueries({ queryKey: ['accounting'] });
  };
  const onError = (e: unknown): void => {
    message.error(e instanceof ApiError ? e.message : t('accounting.codeSaveFailed'));
  };

  const saveCompany = useMutation({
    mutationFn: ({ uid, itemCode }: { uid: string; itemCode: string; saved: () => void }) =>
      setFinanceCompanyCode(uid, itemCode),
    onSuccess: (_data, vars) => { vars.saved(); message.success(t('accounting.codeSaved')); refresh(); },
    onError,
  });
  const saveFee = useMutation({
    mutationFn: ({ feeType, itemCode }: { feeType: FeeCode['feeType']; itemCode: string; saved: () => void }) =>
      setFeeCode(feeType, itemCode),
    onSuccess: (_data, vars) => { vars.saved(); message.success(t('accounting.codeSaved')); refresh(); },
    onError,
  });

  return (
    <Flex vertical gap={16}>
      <Card size="small" title={t('accounting.financeCodes.title')}>
        <Flex vertical gap={12}>
          <Text type="secondary">{t('accounting.financeCodes.help')}</Text>
          {companies.error !== null ? (
            sectionError(companies.error, t('accounting.financeCodes.forbidden'), t('accounting.codesLoadFailed'))
          ) : (companies.data ?? []).length === 0 && !companies.isLoading ? (
            <Text type="secondary">{t('accounting.financeCodes.empty')}</Text>
          ) : (
            (companies.data ?? []).map((c: FinanceCompany) => (
              <CodeRow
                key={c.uid}
                name={c.name}
                current={c.autocountItemCode}
                missing={t('accounting.financeCodes.missing', { name: c.name })}
                maxLength={30}
                saving={saveCompany.isPending && saveCompany.variables.uid === c.uid}
                onSave={(itemCode, saved) => { saveCompany.mutate({ uid: c.uid, itemCode, saved }); }}
              />
            ))
          )}
        </Flex>
      </Card>

      <Card size="small" title={t('accounting.feeCodes.title')}>
        <Flex vertical gap={12}>
          <Text type="secondary">{t('accounting.feeCodes.help')}</Text>
          {fees.error !== null ? (
            sectionError(fees.error, t('accounting.feeCodes.forbidden'), t('accounting.codesLoadFailed'))
          ) : (
            (fees.data ?? []).map((f: FeeCode) => {
              const name = t(`quotation.lineTypes.${f.feeType}`);
              return (
                <CodeRow
                  key={f.feeType}
                  name={name}
                  current={f.itemCode}
                  missing={t('accounting.feeCodes.missing', { name })}
                  maxLength={60}
                  saving={saveFee.isPending && saveFee.variables.feeType === f.feeType}
                  onSave={(itemCode, saved) => { saveFee.mutate({ feeType: f.feeType, itemCode, saved }); }}
                />
              );
            })
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
